import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Mail, ArrowLeft, Shield, CheckCircle, AlertCircle } from 'lucide-react';

type ForgotPasswordFormData = {
  email: string;
};

const ForgotPasswordPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block relative w-1/2">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80"
          alt="Admin Dashboard"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 to-neutral-900/50 mix-blend-multiply" />
        <div className="absolute inset-0 flex flex-col justify-center px-12">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-12 w-12 rounded-lg bg-[#0066FF] flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">
              Administration
            </h1>
          </div>
          <p className="text-xl text-neutral-200 mb-8">
            Réinitialisation de votre mot de passe
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="h-12 w-12 rounded-lg bg-[#0066FF] flex items-center justify-center">
                <Mail className="h-6 w-6 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900">
              Mot de passe oublié
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              Entrez votre adresse email pour recevoir un lien de réinitialisation
            </p>
          </div>

          {success ? (
            <div className="mt-8">
              <div className="flex items-start p-4 border border-emerald-100 rounded-lg bg-emerald-50">
                <CheckCircle className="h-5 w-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-emerald-800">Email envoyé avec succès</h3>
                  <p className="mt-1 text-sm text-emerald-700">
                    Si un compte existe avec cette adresse email, vous recevrez un lien de réinitialisation dans quelques minutes. Vérifiez également votre dossier spam.
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="flex items-center justify-center text-sm font-medium text-[#0066FF] hover:text-[#0066FF]"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour à la connexion
                </Link>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mt-6 flex items-center p-4 border border-red-100 rounded-lg bg-red-50">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700">
                    Adresse email
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-neutral-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className={`pl-10 block w-full rounded-lg border ${
                        errors.email ? 'border-red-300' : 'border-neutral-300'
                      } px-3 py-2 text-neutral-900 placeholder-neutral-500 focus:border-[#0066FF] focus:ring-[#0066FF]/20 sm:text-sm`}
                      placeholder="votre@email.com"
                      {...register('email', {
                        required: "L'email est requis",
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: 'Veuillez entrer une adresse email valide',
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  isLoading={loading}
                  leftIcon={<Mail className="h-5 w-5" />}
                >
                  Envoyer le lien de réinitialisation
                </Button>
              </form>

              <div className="mt-6">
                <Link
                  to="/login"
                  className="flex items-center justify-center text-sm font-medium text-[#0066FF] hover:text-[#0066FF]"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour à la connexion
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
